const PRODUCT_QUERY_KEYS = ['cluster', 'product'] as const;

const RESERVATION_DETAIL_QUERY_KEYS = [
	'arrival_date',
	'bath_card_id',
	'checkout',
	'checkout_type',
	'departure_date',
	'nights',
	'persons',
	'persons_count',
	'voucher_code',
	'voucher_codes',
] as const;

/**
 * Tracking (ga_origin, _gl) and presentation (lang, ro) parameters are deliberately absent:
 * they accompany almost every visit, so counting them as reservation intent would reset the
 * flow on every boot and make resuming impossible.
 */
const RESERVATION_QUERY_KEYS = [...PRODUCT_QUERY_KEYS, ...RESERVATION_DETAIL_QUERY_KEYS] as const;

type ReservationQueryKey = (typeof RESERVATION_QUERY_KEYS)[number];

const hasValue = (value: unknown): boolean => {
	if (Array.isArray(value)) return value.some(hasValue);

	return value !== undefined && value !== null && value !== '';
};

export function shouldStartNewFlow(query: Record<string, unknown>): boolean {
	return RESERVATION_QUERY_KEYS.some((key) => hasValue(query[key]));
}

/** What a query can be held against: the choices the reservation itself records. */
export type ReservationSnapshot = {
	productId: number | undefined;
	arrivalDate: string | null | undefined;
	departureDate: string | null | undefined;
	personsCount: number | undefined;
};

/**
 * Date and party size are held against the reservation, so a link repeating what the guest
 * already chose asks nothing. The remaining details, a voucher or a bath card, are not
 * recorded on the reservation, so their presence has to count as a change: resuming while
 * quietly dropping one is worse than one question too many.
 */
const COMPARED_AGAINST_RESERVATION = ['arrival_date', 'departure_date', 'persons_count'] as const;

type ComparedKey = (typeof COMPARED_AGAINST_RESERVATION)[number];

const sameValue = (requested: unknown, held: unknown): boolean => String(requested) === String(held ?? '');

/**
 * The one place a product id is read from a query. Comparing a request against a reservation
 * has to read it exactly as the flow reads it when loading, otherwise the two disagree about
 * which product was asked for and the guest is questioned about a product that will not change.
 */
export function parseProductId(value: unknown): number | undefined {
	if (!hasValue(value)) return undefined;

	const productId = Number.parseInt(String(value), 10);

	return Number.isNaN(productId) ? undefined : productId;
}

export function isDifferentReservationRequest(
	query: Record<string, unknown>,
	reservation: ReservationSnapshot,
): boolean {
	const held: Record<ComparedKey, unknown> = {
		arrival_date: reservation.arrivalDate,
		departure_date: reservation.departureDate,
		persons_count: reservation.personsCount,
	};

	const asksSomethingElse = RESERVATION_DETAIL_QUERY_KEYS.some((key) => {
		if (!hasValue(query[key])) return false;

		const compared = COMPARED_AGAINST_RESERVATION.find((known) => known === key);

		return compared === undefined || !sameValue(query[key], held[compared]);
	});

	if (asksSomethingElse) return true;

	const requestedProductId = parseProductId(PRODUCT_QUERY_KEYS.map((key) => query[key]).find(hasValue));
	if (requestedProductId === undefined) return false;

	return requestedProductId !== reservation.productId;
}

export function withoutReservationParams<T extends Record<string, unknown>>(query: T): Partial<T> {
	return Object.fromEntries(
		Object.entries(query).filter(([key]) => !RESERVATION_QUERY_KEYS.includes(key as ReservationQueryKey)),
	) as Partial<T>;
}
